import { memo, useCallback } from 'react';

import { CheckIcon } from '@air/design-icons';
import { ComboboxItem } from '@air/primitive-combobox';

import { getFontFamilyCssValue } from '~/hooks/useFontLoader';
import { useFontPreviewWhenVisible } from '~/hooks/useFontPreviewWhenVisible';

export type SearchableBuiltInFontSelectOptionProps = {
  fontFamily: string;
  isSelected: boolean;
  onSelect: (fontFamily: string) => void;
};

/**
 * One built-in family in the font combobox, its label rendered in the family itself. The font file
 * loads lazily when the option scrolls into view (glyph-subset to the label), so the label renders
 * the sans-serif fallback until the swap lands — never a blocking fetch of the whole list's fonts.
 */
export const SearchableBuiltInFontSelectOption = memo(
  ({ fontFamily, isSelected, onSelect }: SearchableBuiltInFontSelectOptionProps) => {
    const previewRef = useFontPreviewWhenVisible(fontFamily);

    const handleSelect = useCallback(() => {
      onSelect(fontFamily);
    }, [fontFamily, onSelect]);

    return (
      <ComboboxItem onSelect={handleSelect} value={fontFamily}>
        {isSelected ? (
          <CheckIcon size="sm" className="shrink-0 ads:text-icon-primary text-blue-9" />
        ) : (
          <div className="size-4 shrink-0" />
        )}
        {/* Dynamic runtime family — inline style is the only option. */}
        <span ref={previewRef} style={{ fontFamily: getFontFamilyCssValue(fontFamily) }}>
          {fontFamily}
        </span>
      </ComboboxItem>
    );
  },
);

SearchableBuiltInFontSelectOption.displayName = 'SearchableBuiltInFontSelectOption';
